// src/pages/tinder.js
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { translations, artworks as artworksMock } from "../data/mock";
import Turnstile from "../components/Turnstile";
import { tinderAPI } from "../services/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "../styles/portfolio.css";

const getWorkImages = (work) =>
  Array.isArray(work.images) && work.images.length > 0
    ? work.images
    : [work.image];

const TinderPage = () => {
  const { language, t } = useLanguage();
  const formLabels = translations.tinder.form;
  const turnstileRef = useRef(null);
  const securityLabels = {
    turnstileRequired: {
      it: "Completa la verifica antispam.",
      en: "Please complete the spam protection check.",
    },
    rateLimit: {
      it: "Hai inviato troppe richieste. Attendi qualche minuto e riprova.",
      en: "You have sent too many requests. Please wait a few minutes and try again.",
    },
  };

  // opere T.I.N.D.E.R. condivise con l'array principale di artworks
  const tinderWorks = artworksMock.filter((w) => w.category === "tinder");

  // in galleria mostrate in ordine inverso: Progetto 4 -> 3 -> 2 -> 1
  const tinderWorksReversed = tinderWorks.slice().reverse();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    requestType: "existing_artwork",
    artworkReference: "",
    message: "",
    website: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [galleryImageIndexes, setGalleryImageIndexes] = useState({});

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const changeGalleryImage = (work, direction) => {
    const images = getWorkImages(work);
    setGalleryImageIndexes((currentIndexes) => {
      const currentIndex = currentIndexes[work.id] || 0;
      return {
        ...currentIndexes,
        [work.id]: (currentIndex + direction + images.length) % images.length,
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (status.error) {
      setStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const handleTurnstileToken = (token) => {
    setTurnstileToken(token);
    if (token && status.error) {
      setStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();

    if (!name || !email || !message) {
      setStatus({ loading: false, success: false, error: t(formLabels.required) });
      return;
    }

    if (!turnstileToken) {
      setStatus({
        loading: false,
        success: false,
        error: t(securityLabels.turnstileRequired),
      });
      return;
    }

    try {
      setStatus({ loading: true, success: false, error: "" });

      await tinderAPI.submit({
        name,
        email,
        request_type: formData.requestType,
        artwork_reference:
          formData.requestType === "existing_artwork"
            ? formData.artworkReference || null
            : null,
        message,
        website: formData.website,
        turnstile_token: turnstileToken,
      });

      setStatus({ loading: false, success: true, error: "" });

      setFormData({
        name: "",
        email: "",
        requestType: "existing_artwork",
        artworkReference: "",
        message: "",
        website: "",
      });
      turnstileRef.current?.reset();
    } catch (error) {
      turnstileRef.current?.reset();
      setStatus({
        loading: false,
        success: false,
        error:
          error.status === 429
            ? t(securityLabels.rateLimit)
            : t(formLabels.error),
      });
    }
  };

  return (
    <div className="page-offset">
      <section className="section-spacing">
        <div className="container">
          <div className="tinder-layout">
            <div className="tinder-images-row">
              <div className="tinder-image">
                <img
                  src="/media/tinder/tinder-logo-stencil.png"
                  alt="TIN DER — logo stencil"
                />
              </div>

              <div className="tinder-image">
                <img
                  src="/media/tinder/tinder-acronym-stencil.png"
                  alt="TIN-DER: Transposition, Identity, Normalized, Data, Errors, Residues"
                />
              </div>
            </div>

            <div className="tinder-text">
              <p className="body-text" style={{ marginBottom: "var(--spacing-md)" }}>
                {t(translations.tinder.intro1)}
              </p>
              <p className="body-text" style={{ marginBottom: "var(--spacing-md)" }}>
                {t(translations.tinder.intro2)}
              </p>
              <p className="body-text" style={{ marginBottom: "var(--spacing-md)" }}>
                {t(translations.tinder.intro3)}
              </p>
              <p className="body-text" style={{ marginBottom: 0 }}>
                {t(translations.tinder.intro4)}
              </p>
            </div>

            <div className="tinder-gallery">
              {tinderWorksReversed.map((work, index) => {
                const images = getWorkImages(work);
                const currentImageIndex = galleryImageIndexes[work.id] || 0;

                return (
                  <div
                    key={work.id}
                    className="artwork-card fade-in-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div
                      className={`tinder-gallery-image-box${
                        images.length > 1
                          ? " tinder-gallery-image-box--carousel"
                          : ""
                      }`}
                    >
                      <Link
                        to={`/${language}/works/${work.id}`}
                        className="tinder-gallery-image-link"
                      >
                        <img
                          src={images[currentImageIndex]}
                          alt={`${t(work.title)}${
                            images.length > 1
                              ? ` — ${t({ it: "foto", en: "photo" })} ${
                                  currentImageIndex + 1
                                }`
                              : ""
                          }`}
                          className="tinder-gallery-image"
                        />
                      </Link>

                      {images.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="artwork-carousel-arrow artwork-carousel-arrow--left"
                            onClick={() => changeGalleryImage(work, -1)}
                            aria-label={t({
                              it: `Foto precedente di ${t(work.title)}`,
                              en: `Previous photo of ${t(work.title)}`,
                            })}
                          >
                            <ChevronLeft size={24} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="artwork-carousel-arrow artwork-carousel-arrow--right"
                            onClick={() => changeGalleryImage(work, 1)}
                            aria-label={t({
                              it: `Foto successiva di ${t(work.title)}`,
                              en: `Next photo of ${t(work.title)}`,
                            })}
                          >
                            <ChevronRight size={24} aria-hidden="true" />
                          </button>
                          <span
                            className="artwork-carousel-counter"
                            aria-live="polite"
                          >
                            {currentImageIndex + 1} / {images.length}
                          </span>
                        </>
                      )}
                    </div>

                    <Link to={`/${language}/works/${work.id}`}>
                      <div className="artwork-info" style={{ textAlign: "center" }}>
                        <span className="category-badge">{t(work.title)}</span>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>

            <div className="tinder-form-wrap" id="tinder-form">
              <div className="contact-form-header">
                <span className="small-text contact-label">{t(formLabels.title)}</span>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-honeypot" aria-hidden="true">
                  <label htmlFor="tinder-website">Website</label>
                  <input
                    id="tinder-website"
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    maxLength={200}
                  />
                </div>

                <div className="contact-form-row">
                  <div className="contact-field">
                    <label htmlFor="tinder-name" className="small-text">
                      {t(formLabels.name)}
                    </label>
                    <input
                      id="tinder-name"
                      name="name"
                      type="text"
                      className="contact-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t(formLabels.namePlaceholder)}
                      maxLength={160}
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="tinder-email" className="small-text">
                      {t(formLabels.email)}
                    </label>
                    <input
                      id="tinder-email"
                      name="email"
                      type="email"
                      className="contact-input"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t(formLabels.emailPlaceholder)}
                      maxLength={254}
                      required
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <span className="small-text" style={{ display: "block", marginBottom: "0.45rem", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    {t(formLabels.requestType)}
                  </span>
                  <div className="tinder-radio-row">
                    <label className="tinder-radio-option">
                      <input
                        type="radio"
                        name="requestType"
                        value="existing_artwork"
                        checked={formData.requestType === "existing_artwork"}
                        onChange={handleChange}
                      />
                      <span className="small-text">{t(formLabels.requestTypeExisting)}</span>
                    </label>

                    <label className="tinder-radio-option">
                      <input
                        type="radio"
                        name="requestType"
                        value="commission"
                        checked={formData.requestType === "commission"}
                        onChange={handleChange}
                      />
                      <span className="small-text">{t(formLabels.requestTypeCommission)}</span>
                    </label>
                  </div>
                </div>

                {formData.requestType === "existing_artwork" && (
                  <div className="contact-field">
                    <label htmlFor="tinder-artwork" className="small-text">
                      {t(formLabels.artworkLabel)}
                    </label>
                    <select
                      id="tinder-artwork"
                      name="artworkReference"
                      className="contact-input"
                      value={formData.artworkReference}
                      onChange={handleChange}
                    >
                      <option value="">{t(formLabels.artworkGeneric)}</option>
                      {tinderWorks
                        .filter((work) => work.available === true)
                        .map((work) => (
                          <option key={work.id} value={`progetto-${work.id}`}>
                            {t(work.title)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="contact-field">
                  <label htmlFor="tinder-message" className="small-text">
                    {t(formLabels.message)}
                  </label>
                  <textarea
                    id="tinder-message"
                    name="message"
                    className="contact-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t(formLabels.messagePlaceholder)}
                    rows={6}
                    maxLength={5000}
                    required
                  />
                </div>

                <Turnstile
                  ref={turnstileRef}
                  action="tinder_request"
                  language={language}
                  onTokenChange={handleTurnstileToken}
                />

                <div className="contact-actions">
                  <button
                    type="submit"
                    disabled={status.loading}
                    className="btn btn-outline"
                    style={{
                      minWidth: 160,
                      justifyContent: "center",
                      opacity: status.loading ? 0.7 : 1,
                    }}
                  >
                    {status.loading ? t(formLabels.sending) : t(formLabels.send)}
                  </button>

                  {status.success && (
                    <span className="small-text contact-status-success">
                      {t(formLabels.success)}
                    </span>
                  )}

                  {status.error && (
                    <span className="small-text contact-status-error">
                      {status.error}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TinderPage;
